import { useQuery } from "@tanstack/react-query";
import { getBillingStatus } from "@/api/client";
import type { BillingStatus } from "@/api/client/types";

/**
 * What the current user is allowed to do, derived on the backend and read here.
 *
 * Every limit in here is also enforced server-side — these values drive the UI only. The
 * research cap in particular must never be trusted client-side, because the stock page keeps
 * its view mode and period in URL parameters.
 *
 * Shares the "billing-info-status" query key with BillingGate, BillingInfoModal, AuthListener
 * and SubscriptionCard so all of them read from a single deduplicated /me/billing fetch.
 */
export function useEntitlements() {
  const { data, isLoading } = useQuery<BillingStatus>({
    queryKey: ["billing-info-status"],
    queryFn: getBillingStatus,
  });

  // Until the status arrives, assume full access. Showing a paid feature for a moment and then
  // gating it is far better than flashing an upgrade prompt at a paying user on every load.
  const hasPaidAccess = data?.entitlements?.has_paid_access ?? true;

  return {
    isLoading,
    tier: data?.tier ?? "paid",
    isPaid: hasPaidAccess,
    isFree: !hasPaidAccess,
    isFounding: data?.is_founding_member ?? false,
    /** End of the app trial. Only meaningful while `tier` is "trial". */
    trialEndsAt: data?.trial_ends_at ?? null,
    freeTierEnabled: data?.free_tier_enabled ?? false,
    hasChosenFreeTier: data?.free_tier_choice_at != null,

    maxPortfolios: data?.entitlements?.max_portfolios ?? 20,
    maxWatchlists: data?.entitlements?.max_watchlists ?? 20,
    maxWatchlistItems: data?.entitlements?.max_watchlist_items ?? 200,
    /** Null means unrestricted: full quarterly and TTM history. */
    researchAnnualYears: data?.entitlements?.research_annual_years ?? null,

    // Named per feature so call sites read as intent rather than as a tier check.
    canSyncBrokers: hasPaidAccess,
    canUseTradeAnalysis: hasPaidAccess,
    canUseTaxCalculator: hasPaidAccess,
    canUseTranscripts: hasPaidAccess,
    canUseCharts: hasPaidAccess,
    canUseEarningsCalendar: hasPaidAccess,
    canUseNews: hasPaidAccess,
    canUseDcf: hasPaidAccess,
  };
}
